import {
  TrendingUp,
  Trophy,
  ArrowUpRight,
  ArrowRight,
  Dribbble,
  Brain,
  Zap,
  Dumbbell,
  Users,
  Tent,
  HeartPulse,
  Activity,
  Gauge,
  Clapperboard,
  Headphones,
  BookOpen,
  FlaskConical,
  GraduationCap,
  ClipboardList,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { images } from "@/data/images";
import { resume } from "@/data/resume";
import { NextChapter } from "@/components/NextChapter";

const trainingIcons = { Dribbble, Brain, Zap, Dumbbell, Users, TrendingUp };
const hooperIcons = { Sparkles, Dribbble, Users, GraduationCap };
const eduIcons = {
  HeartPulse,
  Dumbbell,
  Activity,
  Brain,
  Gauge,
  Clapperboard,
  Headphones,
  BookOpen,
  Users,
  FlaskConical,
  GraduationCap,
  ClipboardList,
  Sparkles,
  Zap,
};

const RecordChip = ({ label, value, strong }) => (
  <div
    className={`border px-4 py-2 ${
      strong ? "border-burnt/40 bg-burnt/10" : "border-white/10 bg-ink-base"
    }`}
  >
    <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500">
      {label}
    </span>
    <span className={`font-mono ${strong ? "text-white text-lg" : "text-zinc-300"}`}>
      {value}
    </span>
  </div>
);

const WinGrowthChart = ({ seasons, peak }) => {
  const MAX = 168;
  return (
    <div data-testid="win-growth-chart">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
          League wins by season
        </p>
        <p className="font-mono text-xs text-gold">0 → 10</p>
      </div>
      <div className="grid grid-cols-5 gap-2 sm:gap-4 items-end" style={{ height: MAX + 34 }}>
        {seasons.map((s, i) => {
          const px = Math.max((s.leagueWins / peak) * MAX, 4);
          const isPeak = s.leagueWins === peak;
          return (
            <div key={s.season} className="flex flex-col items-center justify-end h-full">
              <span className="font-mono text-sm md:text-base text-white mb-2">
                {s.leagueWins}
              </span>
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: px }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: "easeOut" }}
                className={`w-full max-w-[54px] mx-auto rounded-t ${
                  isPeak
                    ? "bg-gradient-to-t from-burnt to-gold"
                    : "bg-gradient-to-t from-burnt/60 to-burnt"
                }`}
              />
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-5 gap-2 sm:gap-4 mt-3 border-t border-white/10 pt-3">
        {seasons.map((s) => (
          <span
            key={s.season}
            className="text-center font-mono text-[10px] md:text-xs text-zinc-500"
          >
            {s.season}
          </span>
        ))}
      </div>
    </div>
  );
};

const SeasonCard = ({ s, index, prevWins }) => {
  const delta = prevWins === null ? null : s.leagueWins - prevWins;
  return (
    <Reveal delay={index * 0.05}>
      <div className="relative pl-8 md:pl-10">
        <span className="absolute left-[-7px] top-2 h-3.5 w-3.5 rounded-full border-2 border-burnt bg-ink-base" />
        <div
          data-testid={`season-card-${index}`}
          className="bg-ink-surface border border-white/10 p-6 md:p-7 transition-colors duration-300 hover:border-burnt/40"
        >
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div>
              <p className="font-mono text-gold text-sm">{s.season}</p>
              <h4 className="mt-0.5 font-heading text-lg md:text-xl font-semibold text-white">
                {s.team}
              </h4>
            </div>
            {s.badge && (
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] bg-burnt/15 text-burnt px-3 py-1.5 border border-burnt/30">
                <Trophy size={13} />
                {s.badge}
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-stretch gap-2">
            <RecordChip label="League" value={s.regular} strong />
            {s.exhibition && <RecordChip label="Exhibition" value={s.exhibition} />}
            {delta !== null && delta > 0 && (
              <div className="flex items-center gap-1.5 px-4 py-2 border border-gold/30 bg-gold/5 text-gold">
                <ArrowUpRight size={16} />
                <span className="font-mono text-sm">+{delta} league wins</span>
              </div>
            )}
          </div>

          <ul className="mt-5 space-y-2">
            {s.highlights.map((h, i) => (
              <li key={i} className="flex gap-3 text-zinc-400 text-sm md:text-base leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 bg-burnt shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
};

const BulletList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((p, i) => (
      <li key={i} className="flex gap-3 text-zinc-400 text-sm md:text-base leading-relaxed">
        <span className="mt-2 h-1.5 w-1.5 bg-burnt shrink-0" />
        {p}
      </li>
    ))}
  </ul>
);

// Icon feature card (used in Education parts one & two)
const FeatureCard = ({ icon, title, desc, index }) => {
  const Icon = eduIcons[icon] || Sparkles;
  return (
    <Reveal delay={(index % 3) * 0.06}>
      <div className="group h-full bg-ink-base border border-white/10 p-5 md:p-6 transition-colors duration-300 hover:border-burnt/50">
        <span className="h-11 w-11 grid place-items-center bg-burnt/15 text-burnt transition-colors group-hover:bg-burnt group-hover:text-white">
          <Icon size={20} />
        </span>
        <h4 className="mt-4 font-heading text-base md:text-lg font-semibold text-white leading-snug">
          {title}
        </h4>
        {desc && <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{desc}</p>}
      </div>
    </Reveal>
  );
};

export default function Resume() {
  const { uhill, programGrowth, empower, hoopers, falcons, education } = resume;

  return (
    <div data-testid="resume-page">
      <PageHeader
        eyebrow="Experience"
        title="Experience"
        intro={resume.heroSubtitle}
        image={images.heroCoach}
      />

      {/* COACHING EXPERIENCE — case study */}
      <section className="pt-6 md:pt-10 pb-8 md:pb-12" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <p className="font-mono text-burnt text-sm mb-3">01</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Coaching Experience
            </h2>
            <p className="mt-4 text-zinc-400 max-w-2xl">
              Programs and people, built over time.
            </p>
          </Reveal>

          {/* University Hill — centerpiece */}
          <div id="university-hill" className="mt-12 border border-white/10 bg-ink-surface overflow-hidden">
            {/* Header: team photo + info */}
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-7" data-testid="uhill-image">
                <img
                  src={uhill.image}
                  alt="University Hill Secondary team"
                  className="w-full h-full aspect-[3/2] lg:aspect-auto object-cover object-center"
                />
              </div>
              <div className="lg:col-span-5 p-6 md:p-9 flex flex-col justify-center">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
                  {uhill.years}
                </p>
                <h3 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-white leading-none">
                  {uhill.org}
                </h3>
                <p className="mt-2 text-burnt text-base md:text-lg">{uhill.position}</p>
                <p className="mt-6 text-base md:text-lg text-zinc-300 leading-relaxed border-l-2 border-burnt pl-5">
                  {uhill.tagline}
                </p>
              </div>
            </div>

            {/* PROGRAM GROWTH */}
            <div className="p-6 md:p-10 border-t border-white/10" data-testid="program-growth">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 grid place-items-center bg-burnt/15 text-burnt shrink-0">
                  <TrendingUp size={20} />
                </span>
                <h4 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-white">
                  Program Growth
                </h4>
              </div>
              <p className="mt-3 text-zinc-400 max-w-2xl">{programGrowth.subhead}</p>

              <div className="mt-10 bg-ink-base border border-white/10 p-6 md:p-8">
                <WinGrowthChart
                  seasons={programGrowth.seasons}
                  peak={programGrowth.peakWins}
                />
              </div>

              <div className="mt-12 relative border-l border-white/10 ml-[7px] md:ml-2 space-y-8">
                {programGrowth.seasons.map((s, i) => (
                  <SeasonCard
                    key={s.season}
                    s={s}
                    index={i}
                    prevWins={i === 0 ? null : programGrowth.seasons[i - 1].leagueWins}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* EMPOWER BASKETBALL — portfolio block */}
          <div
            id="empower"
            className="mt-12 border border-white/10 bg-ink-surface overflow-hidden"
            data-testid="empower-section"
          >
            <div className="relative h-[220px] md:h-[320px]" data-testid="empower-banner">
              <img
                src={empower.image}
                alt={empower.org}
                className="w-full h-full object-cover object-[center_25%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-ink-surface/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-9 max-w-3xl">
                <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-none">
                  {empower.org}
                </h3>
                <p className="mt-2 text-burnt text-base md:text-lg">{empower.position}</p>
              </div>
            </div>
            <div className="p-6 md:p-10">
              <p className="max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed border-l-2 border-burnt pl-6">
                {empower.intro}
              </p>

              <div className="mt-10">
                <p className="label-eyebrow mb-5">Quick Facts</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {empower.stats.map((st, i) => (
                    <Reveal key={st.label} delay={(i % 5) * 0.06}>
                      <div className="bg-ink-base border border-white/10 p-4 md:p-5 text-center h-full">
                        <div className="font-heading text-3xl md:text-4xl font-bold text-burnt">
                          {st.value}
                        </div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                          {st.label}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {empower.facts.map((f) => (
                    <span
                      key={f}
                      className="inline-block rounded-full border border-white/15 bg-ink-base px-4 py-1.5 text-sm text-zinc-300"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
                <div>
                  <p className="label-eyebrow mb-5">Athlete Development</p>
                  <BulletList items={empower.development} />
                </div>
                <div>
                  <p className="label-eyebrow mb-5">Training</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {empower.training.map((t) => {
                      const Icon = trainingIcons[t.icon] || Dribbble;
                      return (
                        <div
                          key={t.label}
                          className="flex items-center gap-3 bg-ink-base border border-white/10 px-4 py-3 transition-colors duration-300 hover:border-burnt/40"
                        >
                          <Icon size={18} className="text-burnt shrink-0" />
                          <span className="text-sm md:text-base text-zinc-300">{t.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex gap-4 items-start bg-ink-base border border-white/10 p-5 md:p-6">
                <span className="h-10 w-10 grid place-items-center bg-burnt/15 text-burnt shrink-0">
                  <Tent size={18} />
                </span>
                <div>
                  <p className="font-heading text-lg font-semibold text-white">Camps</p>
                  <p className="mt-1 text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
                    {empower.camps}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* HOOPER'S DELIGHT TRAINING — individual, invite-only development */}
          <div
            id="hoopers-delight"
            className="mt-12 border border-white/10 bg-ink-surface overflow-hidden"
            data-testid="hoopers-section"
          >
            {/* Editorial split — photo + intro */}
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch">
              {/* Photo with cinematic vignette (athlete stays clear, edges fade to black) */}
              <div
                className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[600px] overflow-hidden bg-ink-base"
                data-testid="hoopers-image"
              >
                <img
                  src={images.hoopersTraining}
                  alt="A Hooper's Delight Training athlete on the court"
                  loading="lazy"
                  className="w-full h-full object-cover object-[center_55%]"
                />
                {/* Radial vignette — center clear around the athlete, corners/edges near-black */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(120% 95% at 50% 42%, transparent 44%, rgba(10,10,10,0.55) 80%, rgba(10,10,10,0.95) 100%)",
                  }}
                />
                {/* Blend edges into the surrounding panel */}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ink-surface/75 via-transparent to-transparent" />
                <div className="absolute inset-0 pointer-events-none hidden lg:block bg-gradient-to-r from-transparent via-transparent to-ink-surface/85" />
              </div>
              {/* Heading / role / intro */}
              <div className="p-6 md:p-10 flex flex-col justify-center">
                <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-none">
                  {hoopers.org}
                </h3>
                <p className="mt-2 text-burnt text-base md:text-lg">{hoopers.position}</p>
                <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed border-l-2 border-burnt pl-5">
                  {hoopers.intro}
                </p>
              </div>
            </div>

            {/* How We Train — full width */}
            <div className="p-6 md:p-10 border-t border-white/10">
              <p className="label-eyebrow mb-5">How We Train</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {hoopers.areas.map((a, i) => {
                  const Icon = hooperIcons[a.icon] || Sparkles;
                  return (
                    <Reveal key={a.title} delay={(i % 2) * 0.06}>
                      <div className="h-full bg-ink-base border border-white/10 p-5 md:p-6 transition-colors duration-300 hover:border-burnt/40">
                        <div className="flex items-center gap-3">
                          <span className="h-9 w-9 grid place-items-center bg-burnt/15 text-burnt shrink-0">
                            <Icon size={17} />
                          </span>
                          <h4 className="font-heading text-lg font-semibold text-white leading-tight">
                            {a.title}
                          </h4>
                        </div>
                        <p className="mt-3 text-zinc-400 text-sm md:text-base leading-relaxed">
                          {a.desc}
                        </p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>

          {/* WEST COAST FALCONS */}
          <div
            id="falcons"
            className="mt-12 grid grid-cols-1 lg:grid-cols-2 border border-white/10 bg-ink-surface overflow-hidden"
            data-testid="falcons-section"
          >
            <div className="relative overflow-hidden min-h-[240px] lg:min-h-full group">
              <img
                src={falcons.image}
                alt={falcons.org}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-surface/50 to-transparent" />
            </div>
            <div className="p-6 md:p-9">
              <p className="label-eyebrow text-burnt mb-2">{falcons.org}</p>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white leading-tight">
                {falcons.position}
              </h3>

              <div className="mt-5 space-y-2 border-l-2 border-burnt pl-5">
                {falcons.overview.map((line, i) => (
                  <p key={i} className="text-zinc-300 text-sm md:text-base leading-relaxed">
                    {line}
                  </p>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                {falcons.stats.map((st) => (
                  <div
                    key={st.label}
                    className="bg-ink-base border border-white/10 p-4 text-center"
                  >
                    <div className="font-heading text-3xl font-bold text-burnt">
                      {st.value}
                    </div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                      {st.label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {falcons.facts.map((f) => (
                  <span
                    key={f}
                    className="inline-block rounded-full border border-white/15 bg-ink-base px-4 py-1.5 text-sm text-zinc-300"
                  >
                    {f}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <p className="label-eyebrow mb-3">What We Focus On</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {falcons.focus.map((p, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-zinc-400 text-sm md:text-base leading-relaxed"
                    >
                      <span className="mt-2 h-1.5 w-1.5 bg-burnt shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EDUCATION & PROFESSIONAL DEVELOPMENT */}
      <section
        id="education"
        className="py-20 md:py-28 bg-ink-surface border-y border-white/10 mt-16 md:mt-24"
        data-testid="education-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <p className="font-mono text-burnt text-sm mb-3">02</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Education & Professional Development
            </h2>
          </Reveal>

          {/* Part One — Langara */}
          <div className="mt-12">
            <Reveal>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white">
                {education.school}
              </h3>
              <p className="mt-1 text-burnt text-lg">{education.program}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="mt-8 label-eyebrow">Applying Sport Science to Coaching</p>
            </Reveal>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {education.sportScience.map((c, i) => (
                <FeatureCard key={c.title} {...c} index={i} />
              ))}
            </div>
          </div>

          {/* Part Two — Beyond the Classroom */}
          <div className="mt-16 md:mt-20">
            <Reveal>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white">
                Beyond the Classroom
              </h3>
              <p className="mt-2 text-zinc-400 max-w-2xl">
                Great coaches never stop learning. The game evolves, so I do too.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {education.beyondClassroom.map((c, i) => (
                <FeatureCard key={c.title} {...c} index={i} />
              ))}
            </div>
          </div>

          {/* Part Three — Applying What I Learn */}
          <div className="mt-16 md:mt-20">
            <Reveal>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white">
                Applying What I Learn
              </h3>
              <p className="mt-2 text-zinc-400 max-w-2xl">
                Every subject becomes a tool that shapes how I coach.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {education.applying.map((a, i) => {
                const Icon = eduIcons[a.icon] || Sparkles;
                return (
                  <Reveal key={a.from} delay={(i % 2) * 0.08}>
                    <div className="group bg-ink-base border border-white/10 p-6 flex items-center gap-4 md:gap-6 transition-colors duration-300 hover:border-burnt/50">
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                          I study
                        </p>
                        <p className="mt-1 font-heading text-base md:text-lg font-medium text-zinc-300">
                          {a.from}
                        </p>
                      </div>
                      <span className="h-10 w-10 grid place-items-center bg-burnt/15 text-burnt shrink-0 transition-transform group-hover:translate-x-1">
                        <ArrowRight size={18} />
                      </span>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-[0.15em] text-gold">
                          I coach
                        </p>
                        <p className="mt-1 flex items-center gap-2 font-heading text-base md:text-lg font-semibold text-white">
                          <Icon size={16} className="text-burnt shrink-0" />
                          {a.to}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Next chapter → Alumni */}
      <NextChapter to="/legacy" label="See Where They Are Now" />
    </div>
  );
}
